import React, { useEffect, useRef, useState } from "react";
import { LineChart } from "@mui/x-charts";
import { Box, Grid, Typography } from "@mui/material";
import { getServerList } from "../../services/serverService";
import { BASE_URL } from "../../services/client";

const TOKEN = {
  card: "var(--dk-surface, white)",
  border: "var(--dk-border, rgba(0,0,0,0.09))",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  text: "var(--dk-text, #1d1d1f)",
  sub: "var(--dk-text-2, #6e6e73)",
  hint: "var(--dk-text-3, #aeaeb2)",
  blue: "#2563eb",
  purple: "#7c3aed",
  green: "#16a34a",
  orange: "#ea580c",
};
const SF = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif`;

const HISTORY_LIMIT = 30;

type StatPoint = { time: number; cpu: number; memory: number; disk: number; network: number };

// 대시보드에는 서버가 여럿이어도 대표 서버 1대만 요약해서 보여준다.
// 서버 선택/비교가 필요하면 ServerPage(ServerTable+ServerGraph)를 쓴다.
export default function ServerStatusWidget() {
  const [serverIp, setServerIp] = useState<string | null>(null);
  const [current, setCurrent] = useState<StatPoint | null>(null);
  const [history, setHistory] = useState<StatPoint[]>([]);
  const [isOnline, setIsOnline] = useState(false);
  const primaryIpRef = useRef<string | null>(null);

  useEffect(() => {
    let eventSource: EventSource;

    const start = async () => {
      try {
        await getServerList();
      } catch (error) {
        console.error("서버 목록 조회 실패:", error);
      }

      eventSource = new EventSource(`${BASE_URL}/servers/server-stat`);

      eventSource.onmessage = (event) => {
        try {
          const entry = JSON.parse(event.data);
          const contents = Array.isArray(entry.contents) ? entry.contents : [entry];
          if (contents.length === 0) return;

          if (!primaryIpRef.current) {
            primaryIpRef.current = contents[0].client_ip;
            setServerIp(contents[0].client_ip);
          }

          const row = contents.find((r: any) => r.client_ip === primaryIpRef.current) ?? contents[0];
          const point: StatPoint = {
            time: new Date(row.time).getTime(),
            cpu: parseFloat(row.cpu),
            memory: parseFloat(row.memory),
            disk: parseFloat(row.disk),
            network: parseFloat(row.network),
          };

          setCurrent(point);
          setIsOnline(true);
          setHistory((prev) => [...prev, point].slice(-HISTORY_LIMIT));
        } catch (error) {
          console.error("서버 상태 처리 오류:", error);
        }
      };

      eventSource.onerror = () => {
        setIsOnline(false);
        eventSource.close();
      };
    };

    start();

    return () => {
      eventSource?.close();
    };
  }, []);

  const metrics = [
    { label: "CPU 사용률", value: current?.cpu, color: TOKEN.blue },
    { label: "메모리 사용률", value: current?.memory, color: TOKEN.purple },
    { label: "Disk 사용률", value: current?.disk, color: TOKEN.green },
  ];

  const legend = [
    { color: "#dc2626", label: "CPU" },
    { color: TOKEN.orange, label: "메모리" },
    { color: TOKEN.green, label: "Disk" },
    { color: TOKEN.blue, label: "Network" },
  ];

  return (
    <Box
      sx={{
        background: TOKEN.card,
        borderRadius: "14px",
        border: `0.5px solid ${TOKEN.border}`,
        boxShadow: TOKEN.shadow,
        p: "20px",
        mb: "20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "18px" }}>
        <Box>
          <Typography
            sx={{ fontSize: "15px", fontWeight: 700, color: TOKEN.text, letterSpacing: "-0.02em", fontFamily: SF }}
          >
            서버 상태
          </Typography>
          <Typography sx={{ fontSize: "12px", color: TOKEN.sub, mt: "2px", fontFamily: SF }}>
            {serverIp ? `${serverIp} · 실시간 시스템 리소스 모니터링` : "실시간 시스템 리소스 모니터링"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: isOnline ? "#28c840" : "#d1d1d6" }} />
          <Typography
            sx={{ fontSize: "12px", fontWeight: 500, color: isOnline ? "#28a745" : TOKEN.hint, fontFamily: SF }}
          >
            {isOnline ? "온라인" : "연결 대기"}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {metrics.map((m) => (
          <Grid item xs={4} key={m.label}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}>
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "var(--dk-text-2, #3a3a3c)", fontFamily: SF }}
              >
                {m.label}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: m.color, fontFamily: SF }}>
                {m.value !== undefined ? `${m.value}%` : "-"}
              </Typography>
            </Box>
            <Box sx={{ height: 4, background: "var(--dk-surface-3, #e8e8ed)", borderRadius: "99px", overflow: "hidden", mb: "5px" }}>
              <Box sx={{ height: 4, width: `${m.value ?? 0}%`, background: m.color, borderRadius: "99px" }} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "11px", color: TOKEN.hint, fontFamily: SF }}>0%</Typography>
              <Typography sx={{ fontSize: "11px", color: TOKEN.hint, fontFamily: SF }}>100%</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: "16px", pt: "16px", borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.07))" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "14px", mb: "10px" }}>
          {legend.map((l) => (
            <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: l.color }} />
              <Typography sx={{ fontSize: "11px", color: TOKEN.sub, fontFamily: SF }}>{l.label}</Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            height: 200,
            background: "var(--dk-surface-2, #fafafa)",
            borderRadius: "10px",
            border: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
            overflow: "hidden",
            display: history.length >= 2 ? "block" : "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {history.length >= 2 ? (
            <LineChart
              height={200}
              margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
              leftAxis={null}
              bottomAxis={null}
              xAxis={[{ data: history.map((h) => h.time), scaleType: "time" }]}
              series={[
                { data: history.map((h) => h.cpu), color: "#dc2626", showMark: false },
                { data: history.map((h) => h.memory), color: TOKEN.orange, showMark: false },
                { data: history.map((h) => h.disk), color: TOKEN.green, showMark: false },
                { data: history.map((h) => h.network), color: TOKEN.blue, showMark: false },
              ]}
              slotProps={{ legend: { hidden: true } }}
            />
          ) : (
            <Typography sx={{ fontSize: "12px", color: TOKEN.hint, fontFamily: SF }}>데이터 수집 중...</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
